/**
 * Big Data / Lakehouse – Quản lý 4 nguồn dữ liệu theo project (Production).
 * Route: /workspace/storage/:projectId/lakehouse
 */
import React, { useState } from 'react';
import { Box, Button, IconButton, LinearProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    useGetProjectDataSourcesQuery,
    useCreateProjectDataSourceMutation,
    useUpdateProjectDataSourceMutation,
    useDeleteProjectDataSourceMutation,
    useLazyFetchProjectDataSourceQuery,
    useSyncLakehouseSamplesMutation
} from 'store/slices/apiSlice';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { openSnackbar } from 'store/slices/snackbar';
import { useDispatch } from 'react-redux';

import { SOURCE_TYPES } from './constants';
import SourceTypeCard from './components/SourceTypeCard';
import SourceFormDialog from './components/SourceFormDialog';
import DeleteSourceConfirmDialog from './components/DeleteSourceConfirmDialog';
import FetchDataDialog from './components/FetchDataDialog';

const ProjectLakehouseDataSources = ({ projectName, projectSubname, onBack }) => {
    const dispatch = useDispatch();
    const {
        data: sourcesList = [],
        isLoading,
        isError,
        refetch
    } = useGetProjectDataSourcesQuery(projectName, { skip: !projectName });

    const [createSource, { isLoading: isCreating }] = useCreateProjectDataSourceMutation();
    const [updateSource, { isLoading: isUpdating }] = useUpdateProjectDataSourceMutation();
    const [deleteSource, { isLoading: isDeleting }] = useDeleteProjectDataSourceMutation();
    const [syncSamples, { isLoading: isSyncing }] = useSyncLakehouseSamplesMutation();
    const [fetchData, { data: fetchResult, isLoading: fetchLoading, error: fetchError }] = useLazyFetchProjectDataSourceQuery();

    const [formOpen, setFormOpen] = useState(false);
    const [formSourceType, setFormSourceType] = useState('mongodb');
    const [editingSource, setEditingSource] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [sourceToDelete, setSourceToDelete] = useState(null);

    const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
    const [sourceToFetch, setSourceToFetch] = useState(null);

    const sourcesByType = React.useMemo(() => {
        const map = { mongodb: [], postgres: [], minio: [], firebase: [] };
        (sourcesList || []).forEach((s) => {
            if (map[s.source_type]) map[s.source_type].push(s);
        });
        return map;
    }, [sourcesList]);

    const handleAdd = (sourceTypeId) => {
        setFormSourceType(sourceTypeId);
        setEditingSource(null);
        setFormOpen(true);
    };

    const handleEdit = (source) => {
        setFormSourceType(source.source_type);
        setEditingSource(source);
        setFormOpen(true);
    };

    const handleSaveSource = async ({ name, config }) => {
        try {
            if (editingSource) {
                await updateSource({
                    projectName,
                    sourceId: editingSource.id,
                    body: { name, config }
                }).unwrap();
                dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Đã cập nhật nguồn',
                        variant: 'alert',
                        alert: { color: 'success' }
                    })
                );
            } else {
                await createSource({
                    projectName,
                    body: { source_type: formSourceType, name, config }
                }).unwrap();
                dispatch(
                    openSnackbar({
                        open: true,
                        message: 'Đã thêm nguồn',
                        variant: 'alert',
                        alert: { color: 'success' }
                    })
                );
            }
            setFormOpen(false);
            refetch();
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.data?.detail || err?.message || 'Có lỗi khi lưu',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    const handleDeleteClick = (source) => {
        setSourceToDelete(source);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!sourceToDelete) return;
        try {
            await deleteSource({ projectName, sourceId: sourceToDelete.id }).unwrap();
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Đã xóa nguồn',
                    variant: 'alert',
                    alert: { color: 'success' }
                })
            );
            setDeleteDialogOpen(false);
            setSourceToDelete(null);
            refetch();
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.data?.detail || err?.message || 'Không xóa được nguồn',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    const handleViewData = (source) => {
        setSourceToFetch(source);
        setFetchDialogOpen(true);
    };

    const handleFetchData = (opts) => {
        fetchData(opts);
    };

    const handleSyncSamples = async () => {
        if (!projectName) return;
        try {
            const res = await syncSamples(projectName).unwrap();
            dispatch(
                openSnackbar({
                    open: true,
                    message: res?.message || `Đã đồng bộ ${res?.synced ?? 0} script mẫu vào work/lakehouse_samples/`,
                    variant: 'alert',
                    alert: { color: 'success' }
                })
            );
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.data?.detail || err?.message || 'Đồng bộ thất bại',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    return (
        <MainCard
            title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <IconButton size="small" onClick={onBack} aria-label="Quay lại">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StorageIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                        <Typography variant="h3" fontWeight={600}>
                            Nguồn dữ liệu – Big Data / Lakehouse
                        </Typography>
                    </Box>
                    {projectSubname && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {projectSubname}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                        <Button
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={() => refetch()}
                            disabled={isLoading}
                        >
                            Làm mới
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleSyncSamples}
                            disabled={isSyncing || isLoading}
                        >
                            {isSyncing ? 'Đang đồng bộ...' : 'Đồng bộ script mẫu'}
                        </Button>
                    </Box>
                </Box>
            }
        >
            {isLoading && <LinearProgress sx={{ mb: 2 }} />}

            {isError && (
                <Box
                    sx={{
                        py: 3,
                        px: 2,
                        textAlign: 'center',
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                        borderRadius: 1,
                        mb: 2
                    }}
                >
                    <Typography>Không tải được danh sách nguồn. Kiểm tra kết nối và quyền truy cập.</Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={() => refetch()}>
                        Thử lại
                    </Button>
                </Box>
            )}

            {!isError && (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Quản lý bốn loại nguồn: MongoDB, PostgreSQL, MinIO, Firebase. Thêm cấu hình kết nối, sửa hoặc xóa, và xem dữ liệu trực tiếp.
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
                            gap: gridSpacing
                        }}
                    >
                        {SOURCE_TYPES.map((st) => (
                            <SourceTypeCard
                                key={st.id}
                                sourceType={st}
                                sources={sourcesByType[st.id]}
                                onAdd={handleAdd}
                                onEdit={handleEdit}
                                onDelete={handleDeleteClick}
                                onViewData={handleViewData}
                            />
                        ))}
                    </Box>
                </>
            )}

            <SourceFormDialog
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditingSource(null); }}
                sourceTypeId={formSourceType}
                editingSource={editingSource}
                onSave={handleSaveSource}
                isSaving={isCreating || isUpdating}
            />

            <DeleteSourceConfirmDialog
                open={deleteDialogOpen}
                source={sourceToDelete}
                onConfirm={handleDeleteConfirm}
                onCancel={() => { setDeleteDialogOpen(false); setSourceToDelete(null); }}
                isDeleting={isDeleting}
            />

            <FetchDataDialog
                open={fetchDialogOpen}
                onClose={() => { setFetchDialogOpen(false); setSourceToFetch(null); }}
                projectName={projectName}
                source={sourceToFetch}
                fetchData={handleFetchData}
                result={fetchResult}
                isLoading={fetchLoading}
                error={fetchError}
            />
        </MainCard>
    );
};

export default ProjectLakehouseDataSources;
